import { useState, useCallback, useEffect } from 'react';
import { useAiAssistantStore } from '../store/useAiAssistantStore';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

export function useAiChat() {
  const store = useAiAssistantStore();
  const location = useLocation();
  const [isInitializing, setIsInitializing] = useState(true);

  // Load chat history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await api.get('/api/ai-chat/history', { timeout: 30000 });
        
        if (response.status === 200) {
          const result = response.data;
          if (result.success && result.data) {
            // Map the server history to the local store format
            const history = result.data.map((item: any) => ({
              id: item.id,
              role: item.role,
              content: item.content,
              timestamp: item.created_at || item.createdAt
            }));
            store.setMessages(history);
            if (result.agentName || result.personaName) {
              store.setAgentInfo(
                result.agentName || '',
                result.personaName || '',
                result.greetingMessage || '',
                result.closingMessage || ''
              );
            }
          }
        }
      } catch (error) {
        console.error('Failed to load AI chat history', error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (store.isOpen && isInitializing) {
      fetchHistory();
    }
  }, [store.isOpen, isInitializing]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // 1. Add user message locally
    const userMessageId = crypto.randomUUID();
    store.addMessage({
      id: userMessageId,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    });

    store.setTyping(true);

    try {
      // 2. Send to backend with context
      const response = await api.post('/api/ai-chat/message', {
        message: content,
        currentPath: location.pathname
      }, { timeout: 60000 });

      if (response.status === 200) {
        const result = response.data;
        if (result.success && result.data) {
          store.addMessage(result.data);
        } else {
          throw new Error(result.message || 'Unknown error');
        }
      } else {
        throw new Error('Server returned an error');
      }
    } catch (error: any) {
      console.error('Failed to send AI message', error);
      store.addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}`,
        timestamp: new Date().toISOString()
      });
    } finally {
      store.setTyping(false);
    }
  }, [location.pathname, store]);

  const clearChatHistory = useCallback(async () => {
    try {
      store.setMessages([]);
      await api.delete('/api/ai-chat/history');
    } catch (error) {
      console.error('Failed to clear history on server', error);
    }
  }, [store]);

  return {
    ...store,
    sendMessage,
    clearChatHistory,
    isInitializing
  };
}

